import React, { useEffect, useMemo, useState, useRef } from 'react';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment-timezone';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router';
import { user } from '../../../redux/user';
import { managementCompany, setManagementCompany } from '../../../redux/managementCompany';
import {
	selectViewStateForManagementCompany,
	updateViewStateForManagementCompany
} from '../../../redux/flexProfessionalsView';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import FlexProfessionalsList from './FlexProfessionalsList';
import MANAGEMENT_COMPANIES_PERMISSIONS from '../../../constants/ManagementCompaniesPermissions';
import arrow from '../../../assets/images/keyboard_arrow_down.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/images/download.svg';
import utils from '../../../utils/utils';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { PERMISSION, PRIVATE_PATH } from '../../../constants';
import TextSearchComponent from '../../../components/SearchComponent/TextSearchComponent';

import './FlexWorkersScreen.scss';

const DEFAULT_FILTERS = { global: { value: '', matchMode: FilterMatchMode.CONTAINS } };

const FlexProfessionals = () => {
	const history = useHistory();
	const dispatch = useDispatch();
	const dataTableRef = useRef(null);

	const userData = useSelector(user, shallowEqual);
	const managementCompanyData = useSelector(managementCompany, shallowEqual);

	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const currentManagementCompanyId = managementCompanyData?.id;
	const viewState = useSelector(selectViewStateForManagementCompany(currentManagementCompanyId));
	const filters = viewState?.filters || DEFAULT_FILTERS;
	const searchTerm = filters.global?.value || '';

	const setFilters = (updater) => {
		if (!currentManagementCompanyId) return;
		const next = typeof updater === 'function' ? updater(filters) : updater;
		dispatch(updateViewStateForManagementCompany({ managementCompanyId: currentManagementCompanyId, filters: next }));
	};

	const managementCompaniesOptions = useMemo(() => {
		const uniqueManagementCompanyIds = new Set();
		const options = [];

		userData?.facilitiesEnabled?.forEach((facility) => {
			const mcId = facility.management_company_id;
			if (
				!uniqueManagementCompanyIds.has(mcId) &&
				utils.isAuthorizedExact(
					MANAGEMENT_COMPANIES_PERMISSIONS.WORKFORCE_ACTION_VIEW,
					userData.managementCompanyPermissions[mcId]
				) &&
				facility.allow_workforce_page
			) {
				uniqueManagementCompanyIds.add(mcId);
				options.push({
					id: mcId,
					name: facility.management_company_name
				});
			}
		});

		return options.sort((a, b) => a.name.localeCompare(b.name));
	}, [userData]);

	const handleManagementCompanyChange = (newManagementCompany) => {
		dispatch(setManagementCompany(newManagementCompany));
	};

	useEffect(() => {
		if (!managementCompaniesOptions.length) {
			history.push(PRIVATE_PATH.SHIFTS);
			return;
		}

		if (!managementCompanyData?.id && managementCompaniesOptions.length) {
			handleManagementCompanyChange(managementCompaniesOptions[0]);
		}
	}, [managementCompaniesOptions]);

	const ManagementCompanyDropdown = () => {
		if (managementCompaniesOptions?.length < 2) {
			return <div className="title">Workforce - {managementCompanyData?.name}</div>;
		}
		return (
			<div className="dropdown-management-company">
				<Dropdown>
					<Dropdown.Toggle>
						<div className="title">Workforce - {managementCompanyData?.name}</div>
						<img src={arrow} alt="expand" className="keyboard-arrow-down-icon" />
					</Dropdown.Toggle>
					<Dropdown.Menu>
						{managementCompaniesOptions.map((option) => (
							<Dropdown.Item
								className="dropdown-management-company__item"
								onClick={() => handleManagementCompanyChange(option)}
								key={`management-company-${option.id}`}
							>
								{option.name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			</div>
		);
	};

	const exportCSV = () => {
		dataTableRef.current.exportCSV();
	};

	const onSearch = (value) => {
		setFilters((prev) => ({
			...prev,
			global: { ...(prev.global || { matchMode: FilterMatchMode.CONTAINS }), value }
		}));
	};

	const exportFilename = `Workforce_${managementCompanyData.name}_${moment().format('YYYY-MM-DD_hh-mm')}`;

	return (
		<WithOnlyOnDesktop>
			<div className="flex-workers-screen">
				<div className="title-wrapper">
					<ManagementCompanyDropdown />
					<TextSearchComponent
						placeholder="Search for..."
						className="search-container"
						searchValue={searchTerm}
						setSearchValue={onSearch}
					/>
				</div>
				<div className="btn-download-container">
					<IsAuthorizedForManagementCompany
						exactPermission={PERMISSION.WORKFORCE_DOWNLOAD}
						managementCompanyId={managementCompanyData.id}
					>
						<Button
							type="button"
							className="btn-download-list"
							label="Download report"
							outlined
							onClick={exportCSV}
							data-pr-tooltip="Download"
							icon={() => <DownloadIcon />}
						/>
					</IsAuthorizedForManagementCompany>
				</div>

				<TabView className="workforce-tab" activeIndex={activeTabIndex} onTabChange={(e) => setActiveTabIndex(e.index)}>
					<TabPanel header="Internal">
						{managementCompanyData?.id && (
							<FlexProfessionalsList
								managementCompany={managementCompanyData}
								dataTableRef={dataTableRef}
								exportFilename={exportFilename}
								filters={filters}
								setFilters={setFilters}
							/>
						)}
					</TabPanel>
				</TabView>
			</div>
		</WithOnlyOnDesktop>
	);
};

export default FlexProfessionals;
